/**
 * DTOs for the stateless camera-service endpoints used by the OEM tray assembly
 * flow (`POST /preview/start`, `POST /preview/stop`, `GET /snapshot`).
 *
 * Like the OEM prediction service this is a standalone microservice: callers
 * resolve its base URL from `VITE_CAMERA_API_URL` and use a plain `fetch`.
 *
 * The stateful streaming/polling detection endpoints (video-sources, clock,
 * inference, socket.io) are intentionally out of scope here.
 */

/** Camera preview input: recorded video file, live camera feed, or static image. */
export enum CameraInputMode {
  VIDEO = 'video',
  CAMERA = 'camera',
  IMAGE = 'image',
}

/** Caller-facing options describing how a preview should be started. */
export interface PreviewStartOptions {
  inputMode: CameraInputMode;
  sourcePath?: string | null;
  sourceIndex?: number | null;
}

/** Request payload sent to `POST /preview/start`. */
export interface PreviewStartPayload {
  kind: CameraInputMode;
  /** Video filename, required only for {@link CameraInputMode.VIDEO}. */
  filename?: string;
}

/**
 * Normalized result of `GET /snapshot`. The endpoint may reply with JSON, a raw
 * image, or a URL as text; the client normalizes all three to this shape.
 */
export interface SnapshotCaptureResult {
  /** Displayable image URL (a service URL or a blob object URL). */
  previewUrl: string;
  /** Backend storage key from the `x-additional-storage-paths` header, if any. */
  storageKey: string | null;
}
